import React, { useState, useContext, useEffect } from "react";
import ApiService from "../../ApiService";
import { Form, Modal } from "react-bootstrap";
import { IoAddCircle, IoRemoveCircle } from "react-icons/io5";
import { BsArrowLeft } from "react-icons/bs";
import { MdErrorOutline } from "react-icons/md";
import { SnackbarContext } from "../AlertSystem";
import { MonitorService } from "../../util/captureException";
import "./ManagePrepEnrollment.css";

// Backend remove API expects Cognito sub (UUID). Prefer UUID-shaped values so we never send slugs like "wasthere".
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(val) {
  return typeof val === "string" && UUID_REGEX.test(val.trim());
}

/** Extract user/sub ID from patient API response. Prefers UUID (e.g. Cognito sub) for clinician APIs. */
function getPatientUserId(data) {
  if (!data) return "";
  const d = typeof data === "object" ? data : {};
  const candidates = [
    d.sub,
    d.userId,
    d.cognitoSub,
    d.subId,
    d.uuid,
    d.id,
    d.personId,
    d.patientId,
  ].filter(Boolean);
  const nested = [d.user, d.patient].filter(Boolean);
  for (const n of nested) {
    const fromNested = getPatientUserId(n);
    if (fromNested) candidates.push(fromNested);
  }
  const asStrings = candidates.map((c) => (typeof c === "string" ? c.trim() : String(c)));
  const uuid = asStrings.find(isUuid);
  if (uuid) return uuid;
  return asStrings[0] || "";
}

/** Pick best user ID from multiple sources (patient details, URL param). Prefers UUID for clinician APIs. */
function effectiveUserIdFrom(...sources) {
  const candidates = sources.filter(Boolean).map((s) => (typeof s === "string" ? s.trim() : String(s))).filter(Boolean);
  const uuid = candidates.find(isUuid);
  return uuid || candidates[0] || "";
}

/** Find first string in object (recursive) that matches UUID. Handles nested / differently named API responses. */
function findFirstUuidInObject(obj, seen = new Set()) {
  if (obj == null || seen.has(obj)) return "";
  if (typeof obj !== "object") return isUuid(obj) ? String(obj).trim() : "";
  seen.add(obj);
  const vals = Array.isArray(obj) ? obj : Object.values(obj);
  for (const v of vals) {
    if (typeof v === "string" && isUuid(v)) return v.trim();
    const nested = findFirstUuidInObject(v, seen);
    if (nested) return nested;
  }
  return "";
}

const ManagePrepEnrollment = ({ resetView, onNavigateToCreatePrepOrder }) => {
  const { showSnackbar } = useContext(SnackbarContext);

  const [patientLoaded, setPatientLoaded] = useState(false);
  const [lookupInput, setLookupInput] = useState("");
  const [lookupBy, setLookupBy] = useState("email");
  const [initialUrlCheckDone, setInitialUrlCheckDone] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);

  const [patientDetail, setPatientDetail] = useState(null);
  const [patientSubId, setPatientSubId] = useState("");

  const urlParams = new URLSearchParams(window.location.search);
  const patientIdFromUrl = urlParams.get("patientId");
  const emailFromUrl = urlParams.get("email");

  const fetchPatientByEmail = async (emailToUse) => {
    const trimmed = (emailToUse || lookupInput || "").trim();
    if (!trimmed) {
      showSnackbar({ message: "Please enter an email address", severity: "warning" });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      showSnackbar({ message: "Please enter a valid email address", severity: "warning" });
      return;
    }
    setIsLoading(true);
    try {
      const res = await ApiService.getPatientDetailByEmail(encodeURIComponent(trimmed));
      if (res?.data) {
        setPatientDetail(res.data);
        const pid = getPatientUserId(res.data);
        const uuidFromResponse = isUuid(pid) ? pid : findFirstUuidInObject(res.data);
        const idForStateAndUrl = uuidFromResponse || pid;
        setPatientSubId(idForStateAndUrl);
        setPatientLoaded(true);
        setLookupInput("");
        const base = `${window.location.pathname}?page=ManagePrepEnrollment`;
        if (idForStateAndUrl && isUuid(idForStateAndUrl)) {
          window.history.replaceState({}, "", `${base}&patientId=${encodeURIComponent(idForStateAndUrl)}`);
        } else {
          window.history.replaceState({}, "", base);
        }
        showSnackbar({ message: "Patient found. You can enroll or remove from PrEP Maintenance below.", severity: "success" });
      } else {
        showSnackbar({ message: "No patient found for this email.", severity: "warning" });
      }
    } catch (error) {
      showSnackbar({ message: "Could not find patient. Check the email or try again.", severity: "error" });
      MonitorService.captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPatientById = async (idToUse) => {
    const trimmed = (idToUse || lookupInput || "").trim();
    if (!trimmed) {
      showSnackbar({ message: "Please enter a Patient ID", severity: "warning" });
      return;
    }
    setIsLoading(true);
    try {
      const res = await ApiService.getPatientDetailsBySubId(trimmed);
      if (res?.data) {
        setPatientDetail(res.data);
        setPatientSubId(trimmed);
        setPatientLoaded(true);
        setLookupInput("");
        const base = `${window.location.pathname}?page=ManagePrepEnrollment`;
        if (trimmed && isUuid(trimmed)) {
          window.history.replaceState({}, "", `${base}&patientId=${encodeURIComponent(trimmed)}`);
        } else {
          window.history.replaceState({}, "", base);
        }
        showSnackbar({ message: "Patient found. You can enroll or remove from PrEP Maintenance below.", severity: "success" });
      } else {
        showSnackbar({ message: "No patient found for this ID.", severity: "warning" });
      }
    } catch (error) {
      showSnackbar({ message: "Could not find patient. Check the ID or try again.", severity: "error" });
      MonitorService.captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLookup = () => {
    if (lookupBy === "email") fetchPatientByEmail();
    else fetchPatientById();
  };

  const handleChangePatient = () => {
    setPatientLoaded(false);
    setPatientDetail(null);
    setPatientSubId("");
    setLookupInput("");
    window.history.replaceState({}, "", `${window.location.pathname}?page=ManagePrepEnrollment`);
    showSnackbar({ message: "Enter patient email or ID to look up.", severity: "info" });
  };

  useEffect(() => {
    if (initialUrlCheckDone) return;
    setInitialUrlCheckDone(true);
    if (emailFromUrl?.trim()) {
      setLookupInput(emailFromUrl.trim());
      fetchPatientByEmail(emailFromUrl.trim());
      return;
    }
    if (patientIdFromUrl?.trim()) {
      setLookupBy("patientId");
      setLookupInput(patientIdFromUrl.trim());
      fetchPatientById(patientIdFromUrl.trim());
      return;
    }
    setPatientLoaded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const effectiveUserId = effectiveUserIdFrom(
    patientSubId,
    getPatientUserId(patientDetail),
    patientIdFromUrl?.trim()
  );

  const handleOrderPrepMaintenanceKit = () => {
    if (!onNavigateToCreatePrepOrder) {
      showSnackbar({ message: "Cannot navigate to Create PrEP Order.", severity: "warning" });
      return;
    }
    const params = { kitName: "PrEP Maintenance" };
    if (patientDetail?.email) params.email = patientDetail.email;
    if (effectiveUserId) params.patientId = effectiveUserId;
    onNavigateToCreatePrepOrder(params);
  };

  const handleRemoveClick = () => {
    if (!effectiveUserId) {
      console.warn("[PrEP Remove] No user ID found in patient data. Keys:", patientDetail ? Object.keys(patientDetail) : []);
      showSnackbar({ message: "Patient ID is missing. Please look up the patient again.", severity: "warning" });
      return;
    }
    setShowRemoveConfirmModal(true);
  };

  const handleConfirmRemove = async () => {
    if (!effectiveUserId) return;
    setIsSubmitting(true);
    setShowRemoveConfirmModal(false);
    const logPrefix = "[PrEP Remove]";
    console.log(`${logPrefix} Request started`, { userId: effectiveUserId });
    try {
      const response = await ApiService.removePrepMaintenance(effectiveUserId);
      const status = response?.status;
      console.log(`${logPrefix} API success`, {
        userId: effectiveUserId,
        status,
        statusOk: status >= 200 && status < 300,
        body: response?.data,
      });
      showSnackbar({ message: "Patient removed from PrEP Maintenance successfully.", severity: "success" });
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      console.error(`${logPrefix} API failed`, {
        userId: effectiveUserId,
        status,
        data,
        message: error?.message,
        error,
      });
      showSnackbar({ message: "Failed to remove patient. Please try again.", severity: "error" });
      MonitorService.captureException(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayName = patientDetail
    ? [patientDetail.firstName || patientDetail.givenName, patientDetail.lastName || patientDetail.familyName].filter(Boolean).join(" ") || "—"
    : "—";

  return (
    <div className="prep-page">
      {/* Top bar: Back (left-aligned) */}
      <div className="prep-top">
        <button type="button" className="prep-back" onClick={resetView}>
          <BsArrowLeft size={16} /> Back
        </button>
      </div>

      {/* Page header */}
      <div className="prep-header">
        <h1 className="prep-title">Manage PrEP Maintenance</h1>
        <p className="prep-subtitle">
          {patientLoaded
            ? "Patient found. Choose an action below."
            : "Find a patient by email or Patient ID to enroll or remove from PrEP Maintenance."}
        </p>
      </div>

      {/* ── Lookup card (no patient loaded yet) ── */}
      {!patientLoaded && (
        <div className="prep-card">
          <div className="prep-card__body">
            {isLoading ? (
              <div className="prep-loading">
                <div className="spinner-border prep-loading__spinner" role="status" aria-label="Loading" />
                <p className="prep-loading__text">Looking up patient…</p>
              </div>
            ) : (
              <>
                <div className="prep-lookup-toggle">
                  <span className="prep-lookup-label">Search by</span>
                  <button
                    type="button"
                    className={`prep-lookup-toggle__btn ${lookupBy === "email" ? "prep-lookup-toggle__btn--active" : ""}`}
                    onClick={() => setLookupBy("email")}
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    className={`prep-lookup-toggle__btn ${lookupBy === "patientId" ? "prep-lookup-toggle__btn--active" : ""}`}
                    onClick={() => setLookupBy("patientId")}
                  >
                    Patient ID
                  </button>
                </div>

                <Form.Group>
                  <label className="prep-lookup-label">
                    {lookupBy === "email" ? "Email address" : "Patient ID"}
                  </label>
                  <Form.Control
                    type={lookupBy === "email" ? "email" : "text"}
                    placeholder={lookupBy === "email" ? "e.g. patient@example.com" : "Enter Patient ID"}
                    value={lookupInput}
                    onChange={e => setLookupInput(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && (e.preventDefault(), handleLookup())}
                    className="prep-lookup-input"
                    autoFocus
                  />
                </Form.Group>

                <button
                  type="button"
                  className="prep-lookup-submit"
                  onClick={handleLookup}
                  disabled={!lookupInput.trim()}
                >
                  Find patient
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Patient loaded — context + actions ── */}
      {patientLoaded && (
        <div className="prep-card">
          {/* Patient context: label + name + meta, anchored left */}
          <div className="prep-patient">
            <div className="prep-patient__info">
              <span className="prep-patient__label">Patient</span>
              <div className="prep-patient__name">{displayName}</div>
              <div className="prep-patient__meta">
                {patientDetail?.email && <span>{patientDetail.email}</span>}
                {patientDetail?.phoneNumber && <span>{patientDetail.phoneNumber}</span>}
              </div>
            </div>
            <button type="button" className="prep-patient__change" onClick={handleChangePatient}>
              Change patient
            </button>
          </div>

          {/* Actions */}
          <div className="prep-actions">
            <h2 className="prep-actions__heading">Actions</h2>

            {/* Primary: Order kit (shown first, more prominent) */}
            {onNavigateToCreatePrepOrder && (
              <div className="prep-action prep-action--primary">
                <div className="prep-action__icon">
                  <IoAddCircle size={20} />
                </div>
                <div className="prep-action__body">
                  <div className="prep-action__title">Order PrEP Maintenance Kit</div>
                  <p className="prep-action__desc">
                    Create an order for this patient to receive a PrEP Maintenance STI test kit.
                  </p>
                  <button
                    type="button"
                    className="prep-btn prep-btn--primary"
                    onClick={handleOrderPrepMaintenanceKit}
                  >
                    <IoAddCircle size={16} /> Order kit
                  </button>
                </div>
              </div>
            )}

            {/* Destructive: Remove from program */}
            <div className="prep-action prep-action--danger">
              <div className="prep-action__icon">
                <IoRemoveCircle size={20} />
              </div>
              <div className="prep-action__body">
                <div className="prep-action__title">Remove from PrEP Maintenance</div>
                <p className="prep-action__desc">
                  Permanently remove this patient from the program. They will no longer be eligible for PrEP Maintenance tests until re-enrolled.
                </p>
                <button
                  type="button"
                  className="prep-btn prep-btn--danger-outline"
                  onClick={handleRemoveClick}
                  disabled={isSubmitting}
                >
                  <IoRemoveCircle size={16} />
                  {isSubmitting ? "Removing…" : "Remove from program"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirmation modal ── */}
      <Modal
        show={showRemoveConfirmModal}
        onHide={() => setShowRemoveConfirmModal(false)}
        centered
        dialogClassName="prep-modal"
      >
        <div className="prep-modal__header">
          <div className="prep-modal__header-icon">
            <MdErrorOutline size={18} />
          </div>
          <h3 className="prep-modal__header-title">Remove from PrEP Maintenance</h3>
        </div>
        <div className="prep-modal__body">
          <p className="prep-modal__body-text">
            Are you sure you want to remove <strong>{displayName}</strong> from the PrEP Maintenance program?
          </p>
          <p className="prep-modal__body-warning">
            This action will immediately revoke eligibility for PrEP Maintenance tests. The patient must be re-enrolled to regain access.
          </p>
        </div>
        <div className="prep-modal__footer">
          <button
            type="button"
            className="prep-btn prep-btn--danger-solid"
            onClick={handleConfirmRemove}
            disabled={isSubmitting}
          >
            <IoRemoveCircle size={16} />
            {isSubmitting ? "Removing…" : "Yes, remove from program"}
          </button>
          <button
            type="button"
            className="prep-btn prep-btn--secondary"
            onClick={() => setShowRemoveConfirmModal(false)}
          >
            Cancel
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ManagePrepEnrollment;
